import { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Building2, 
  Users, 
  ArrowRight, 
  RefreshCw, 
  CheckCircle,
  AlertTriangle,
  Shield,
  UserCheck
} from "lucide-react";

const CityOwnershipPage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [report, setReport] = useState(null);

  const ADMIN_KEY = process.env.REACT_APP_LIVE_UPDATE_KEY || "bizilgo-live-update-key-2024";

  useEffect(() => {
    if (user?.id) {
      fetchReport();
    }
  }, [user]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/cities/ownership-report?admin_id=${user.id}`);
      setReport(res.data);
    } catch (error) {
      console.error("Failed to fetch ownership report:", error);
      toast.error("Failed to load ownership report");
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!confirm(`Are you sure you want to transfer all cities from City Admins and Sub-Admins to ${report?.admin?.name || 'Main Admin'}?\n\nThis will make you the owner of ${report?.cities_to_transfer?.length || 0} cities.`)) {
      return;
    }

    setTransferring(true);
    try {
      const res = await axios.post(
        `${API}/admin/cities/transfer-ownership-to-main-admin?main_admin_id=${user.id}&admin_key=${ADMIN_KEY}`
      );
      toast.success(res.data.message);
      fetchReport(); // Refresh the report
    } catch (error) {
      console.error("Transfer failed:", error);
      toast.error(error.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">City Ownership</h1>
            <p className="text-muted-foreground">Manage city ownership between Main Admin, City Admins, and Sub-Admins</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchReport} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Admin Info */}
      {report?.admin && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">{report.admin.name}</p>
                <p className="text-sm text-muted-foreground">{report.admin.phone} • Main Admin</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Owned by You</p>
                  <p className="text-2xl font-bold text-green-600">{report.summary?.owned_by_main_admin || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={report.summary?.owned_by_city_admins_or_sub_admins > 0 ? "border-yellow-500" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Need Transfer</p>
                  <p className="text-2xl font-bold text-yellow-600">{report.summary?.owned_by_city_admins_or_sub_admins || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
                  <Users className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Owned by Others</p>
                  <p className="text-2xl font-bold text-gray-600">{report.summary?.owned_by_others || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transfer Action */}
      {report?.cities_to_transfer?.length > 0 && (
        <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="w-5 h-5" />
              Cities Need to be Transferred
            </CardTitle>
            <CardDescription>
              These cities are owned by your City Admins or Sub-Admins. Transfer them to yourself so you can see all orders.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {report.cities_to_transfer.slice(0, 20).map((city, idx) => (
                  <Badge key={idx} variant="outline" className="bg-white dark:bg-gray-800">
                    {city.city} 
                    <span className="text-xs text-muted-foreground ml-1">
                      ({city.owner_name})
                    </span>
                  </Badge>
                ))}
                {report.cities_to_transfer.length > 20 && (
                  <Badge variant="secondary">+{report.cities_to_transfer.length - 20} more</Badge>
                )}
              </div>
              
              <Button 
                onClick={handleTransfer} 
                disabled={transferring}
                className="w-full md:w-auto"
                data-testid="transfer-cities-btn"
              >
                {transferring ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Transfer All {report.cities_to_transfer.length} Cities to Me
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Good Message */}
      {report && report.summary?.owned_by_city_admins_or_sub_admins === 0 && (
        <Card className="border-green-500 bg-green-50 dark:bg-green-900/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-semibold text-green-700 dark:text-green-400">All Good!</p>
                <p className="text-sm text-muted-foreground">All your cities are properly owned by you. No transfers needed.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cities Lists */}
      {report && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Your Cities */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Your Cities ({report.cities_owned_by_main_admin?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.cities_owned_by_main_admin?.length > 0 ? (
                  <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                    {report.cities_owned_by_main_admin.map((city, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">{city}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No cities directly owned by you</p>
                )}
              </CardContent>
            </Card>

            {/* Subordinates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-blue-600" />
                  Your Team
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.city_admins?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">City Admins ({report.city_admins.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {report.city_admins.map((ca, idx) => (
                          <Badge key={idx} variant="outline">{ca.name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {report.sub_admins?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Sub-Admins ({report.sub_admins.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {report.sub_admins.map((sa, idx) => (
                          <Badge key={idx} variant="outline">{sa.name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {(!report.city_admins?.length && !report.sub_admins?.length) && (
                    <p className="text-sm text-muted-foreground">No City Admins or Sub-Admins found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cities Owned by Others - NEW SECTION */}
          {report.cities_owned_by_others?.length > 0 && (
            <Card className="border-gray-300 dark:border-gray-600">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  Cities Owned by Other Admins ({report.cities_owned_by_others.length})
                </CardTitle>
                <CardDescription>
                  These cities are owned by admins outside your team hierarchy. Contact them directly or through SuperAdmin to transfer ownership.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                  {report.cities_owned_by_others.map((city, idx) => {
                    const cityName = typeof city === 'string' ? city : city.city || city.name;
                    const ownerName = typeof city === 'object' ? city.owner_name : null;
                    return (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                      >
                        {cityName}
                        {ownerName && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({ownerName})
                          </span>
                        )}
                      </Badge>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && !report && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default CityOwnershipPage;
